import { useReducer, useMemo, useEffect, useRef } from 'react';
import io from 'socket.io-client';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function countReducer(state, action) {
    if (state === void 0) { state = 0; }
    switch (action.type) {
        case 'increment':
            return state + 1;
        case 'decrement':
            return state - 1;
        case 'set':
            return !!action.value || action.value === 0 ? action.value : state;
        case 'reset':
            return 0;
        default:
            return state;
    }
}
var useLPChatCounts = function (config) {
    var _a = useReducer(countReducer, 0), numberOfNewMessages = _a[0], setNumberOfNewMessages = _a[1];
    var _b = useReducer(countReducer, 0), numberOfUsers = _b[0], setNumberOfUsers = _b[1];
    var socket = useMemo(function () {
        return io(config.url, {
            transports: ['websocket']
        });
    }, [config.url]);
    useEffect(function () {
        socket.emit('subscribe to room', config.roomId);
        return function () {
            socket.disconnect();
        };
    }, [config.roomId]);
    useEffect(function () {
        var onNewMessage = function (message) {
            setNumberOfNewMessages({ type: 'increment' });
        };
        socket.on('new message', onNewMessage);
        var onUserJoined = function (message) {
            setNumberOfUsers({ type: 'increment' });
        };
        socket.on('user joined', onUserJoined);
        var onUserLeft = function (user) {
            setNumberOfUsers({ type: 'decrement' });
        };
        socket.on('user left', onUserLeft);
        var onLogin = function (_a) {
            var users = _a.users, messages = _a.messages;
            setNumberOfNewMessages({
                type: 'set',
                value: messages && messages.length
            });
            setNumberOfUsers({ type: 'set', value: users && users.length });
        };
        socket.on('login', onLogin);
        return function () {
            setNumberOfNewMessages({ type: 'reset' });
            setNumberOfUsers({ type: 'reset' });
            socket.removeEventListener('new messages', onNewMessage);
            socket.removeEventListener('login', onLogin);
        };
    }, [socket]);
    return {
        numberOfNewMessages: numberOfNewMessages,
        numberOfUsers: numberOfUsers
    };
};
function reducer(state, action) {
    switch (action.type) {
        case 'add_message':
            if (!action.payload.message) {
                return state;
            }
            return __assign({}, state, { messages: state.messages.concat([action.payload.message]) });
        case 'login':
            return __assign({}, state, { messages: action.payload.messages
                    ? action.payload.messages
                    : state.messages, users: action.payload.users ? action.payload.users : state.users });
        case 'add_user':
            if (!action.payload.user ||
                !state.users.every(function (currUser) { return currUser.id !== action.payload.user.id; })) {
                // to not add multiple times
                return state;
            }
            return __assign({}, state, { users: state.users.concat([action.payload.user]) });
        case 'remove_user':
            return __assign({}, state, { users: state.users.filter(function (currUser) { return action.payload.user.id !== currUser.id; }) });
        case 'add_typing_user':
            if (!action.payload.typingUser ||
                !state.typingUsers.every(function (currUser) { return currUser.id !== action.payload.typingUser.id; })) {
                // to not add multiple times
                return state;
            }
            return __assign({}, state, { typingUsers: state.typingUsers.concat([action.payload.typingUser]) });
        case 'remove_typing_user':
            return __assign({}, state, { typingUsers: state.typingUsers.filter(function (currUser) { return action.payload.typingUser.id !== currUser.id; }) });
        default:
            return state;
    }
}
var initialState = {
    messages: [],
    typingUsers: [],
    users: []
};
var useLPChat = function (config) {
    var _a = useReducer(reducer, initialState), state = _a[0], dispatch = _a[1];
    var addedUserRef = useRef(null);
    var socket = useMemo(function () {
        return io(config.url, {
            transports: ['websocket']
        });
    }, [config.url]);
    var sendMessage = function (message) {
        socket.emit('new message', message, function () {
            dispatch({ type: 'add_message', payload: { message: message } });
        });
    };
    var startTyping = function () {
        socket.emit('typing');
    };
    var stopTyping = function () {
        socket.emit('stop typing');
    };
    var addUser = function (user) {
        socket.emit('add user', user);
        addedUserRef.current = user;
    };
    useEffect(function () {
        socket.emit('subscribe to room', config.roomId);
        return function () {
            socket.disconnect();
        };
    }, []);
    useEffect(function () {
        var onLogin = function (_a) {
            var users = _a.users, messages = _a.messages;
            dispatch({ type: 'login', payload: { messages: messages, users: users } });
        };
        socket.on('login', onLogin);
        var onNewMessage = function (message) {
            dispatch({ type: 'add_message', payload: { message: message } });
        };
        socket.on('new message', onNewMessage);
        var onUserJoined = function (user) {
            dispatch({ type: 'add_user', payload: { user: user } });
        };
        socket.on('user joined', onUserJoined);
        var onUserLeft = function (user) {
            dispatch({ type: 'remove_user', payload: { user: user } });
        };
        socket.on('user left', onUserLeft);
        var onTyping = function (typingUser) {
            dispatch({ type: 'add_typing_user', payload: { typingUser: typingUser } });
        };
        socket.on('typing', onTyping);
        var onStopTyping = function (typingUser) {
            dispatch({ type: 'remove_typing_user', payload: { typingUser: typingUser } });
        };
        socket.on('stop typing', onStopTyping);
        var onReconnect = function () {
            if (addedUserRef.current) {
                socket.emit('subscribe to room', config.roomId, function () {
                    addUser(addedUserRef.current);
                });
            }
        };
        socket.on('reconnect', onReconnect);
        socket.on('connect_error', config.onConnectError);
        socket.on('connect', config.onConnected);
        return function () {
            socket.removeEventListener('login', onLogin);
            socket.removeEventListener('new messages', onNewMessage);
            socket.removeEventListener('user joined', onUserJoined);
            socket.removeEventListener('user left', onUserLeft);
            socket.removeEventListener('typing', onTyping);
            socket.removeEventListener('stop typing', onStopTyping);
            socket.removeEventListener('reconnect', onStopTyping);
            socket.removeEventListener('reconnect', onReconnect);
            socket.removeEventListener('connect_error', config.onConnectError);
            socket.removeEventListener('connect', config.onConnected);
        };
    }, [socket]);
    return {
        addUser: addUser,
        messages: state.messages,
        sendMessage: sendMessage,
        startTyping: startTyping,
        stopTyping: stopTyping,
        users: state.users,
        typingUsers: state.typingUsers
    };
};

export { useLPChat, useLPChatCounts };
